import { showCustomToast } from 'common/components/CustomToast/utils';
import {
  PrimaryButton,
  Stack,
  TooltipHost,
  DefaultButton,
  Modal,
  Text,
} from '@fluentui/react';
import React, { useRef, useState, useCallback } from 'react';
import { CloseButton } from 'common/components/Buttons';
import { useCommonStyles } from 'common/styles';
import SignatureCanvas from 'react-signature-canvas';
import { useStyles } from './index.styles';
import { ColorPickerCallout } from '../../ColorPickerCallout';
import { CreateSignatureProps } from '..';
import { useFormikContext } from 'formik';
import { SignatureValues } from '../../types';

type SignaturePadProps = Pick<CreateSignatureProps, 'onSignatureCreate'>;

export const SignaturePad: React.FC<SignaturePadProps> = ({ ...props }) => {
  const [visible, setVisible] = useState<boolean>(false);
  return (
    <>
      <TooltipHost content={'Create using signature pad'}>
        <DefaultButton
          styles={{ root: { width: 200 } }}
          iconProps={{ iconName: 'PenWorkspace' }}
          text="Handwritten"
          onClick={() => setVisible(true)}
        />
      </TooltipHost>

      {visible && (
        <SignaturePadModal onDismiss={() => setVisible(false)} {...props} />
      )}
    </>
  );
};

type SignaturePadModalProps = Pick<
  CreateSignatureProps,
  'onSignatureCreate'
> & {
  onDismiss: () => void;
};

export const SignaturePadModal: React.FC<SignaturePadModalProps> = ({
  onDismiss,
  onSignatureCreate,
}) => {
  const styles = useStyles();
  const signatureCanvasRef = useRef<any>(null);
  const commonStyles = useCommonStyles();
  const [penColor, setPenColor] = useState('black');
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const { setFieldValue } = useFormikContext<SignatureValues>();

  // Callback ref to detect when the canvas is mounted
  const setCanvasRef = useCallback((canvas: any) => {
    signatureCanvasRef.current = canvas;
    if (canvas) {
      setIsCanvasReady(true);
    }
  }, []);

  const onSubmit = () => {
    if (!isCanvasReady || !signatureCanvasRef.current) {
      showCustomToast({
        content: 'Signature canvas is not ready',
        appearance: 'error',
      });
      return;
    }
    const canVasIsEmpty = signatureCanvasRef.current.isEmpty();
    if (canVasIsEmpty) {
      showCustomToast({
        content: 'Cannot submit empty canvas',
        appearance: 'error',
      });
    } else {
      const dataURL = signatureCanvasRef.current
        .getTrimmedCanvas()
        .toDataURL('image/png');
      setFieldValue('imageUrl', dataURL);
      onDismiss();
    }
  };

  const clearCanvas = () => {
    if (isCanvasReady && signatureCanvasRef.current) {
      signatureCanvasRef.current.off();
      signatureCanvasRef.current.clear();
      signatureCanvasRef.current.fromData([]);
      signatureCanvasRef.current.on();
      setIsDrawing(false);
    }
  };

  // Handle mouse-down to start drawing
  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (isCanvasReady && signatureCanvasRef.current) {
      setIsDrawing(true);
      const canvas = signatureCanvasRef.current.getCanvas();
      const rect = canvas.getBoundingClientRect();
      const point = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      signatureCanvasRef.current.fromData(signatureCanvasRef.current.toData());
    }
  };

  // Handle mouse-up to stop drawing
  const handleMouseUp = () => {
    if (isCanvasReady && signatureCanvasRef.current) {
      setIsDrawing(false);
      signatureCanvasRef.current.fromData(signatureCanvasRef.current.toData());
      signatureCanvasRef.current.off();
      signatureCanvasRef.current.on();
    }
  };

  // Handle mouse-move to draw only if actively drawing
  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (isCanvasReady && signatureCanvasRef.current && isDrawing) {
      const canvas = signatureCanvasRef.current.getCanvas();
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = penColor;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        ctx.lineTo(x, y);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x, y);
      }
    }
  };

  return (
    <Modal onDismiss={onDismiss} isOpen>
      <Stack className={styles.container}>
        <Stack
          horizontal
          horizontalAlign="space-between"
          tokens={{ padding: 25 }}
        >
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <Text variant="xLarge">Create Signature</Text>
            <Text variant="xLarge" className={commonStyles.colorThemePrimary}>
              Signature Pad
            </Text>
          </Stack>
          <CloseButton onClick={onDismiss} />
        </Stack>

        <Stack
          horizontalAlign="center"
          tokens={{ childrenGap: 20, padding: 25 }}
          className={styles.signatureContainer}
        >
          <SignatureCanvas
            ref={setCanvasRef}
            penColor={penColor}
            minWidth={2}
            maxWidth={4}
            throttle={20}
            backgroundColor={'transparent'}
            canvasProps={{
              className: styles.sigPad,
              onMouseDown: handleMouseDown,
              onMouseUp: handleMouseUp,
              onMouseMove: handleMouseMove,
            }}
          />
        </Stack>
        <Stack
          horizontal
          horizontalAlign="space-between"
          tokens={{ padding: 25 }}
        >
          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <ColorPickerCallout onColorChange={setPenColor} />
            <DefaultButton
              text="Clear"
              onClick={clearCanvas}
              disabled={!isCanvasReady}
            />
          </Stack>
          <PrimaryButton
            text="Submit"
            onClick={onSubmit}
            disabled={!isCanvasReady}
          />
        </Stack>
      </Stack>
    </Modal>
  );
};
